import Context from "../../../context/Context";
import FieldNames from "../../../aiim/datasets/FieldNames";
import OfficePlan from "../OfficePlan";
import * as aiimUtil from "../../../aiim/util/aiimUtil";
import * as sourceUtil from "../sourceUtil";
import * as transaction from "./transaction";
import {
  addAttributeEdit,
  appendPersonUnassignment,
  clonePersonFeature,
  cloneUnitFeature,
  fetchRelatedPeople,
  fetchRelatedUnits,
  fetchUtilization,
  makeUnassignPeopleUpdates,
  unitBasedFieldsForPeople
} from "./transactionUtil";

/*
  transactions

    assignPeopleToArea
    assignPeopleToUnit
    unassignPeople

*/

export function prepare(task) {
  const promise = new Promise((resolve,reject) => {

    console.log("people.prepare",task)

    const transactionInfo = transaction.makeTransactionInfo();
    const peopleFeatureItems = task.peopleFeatureItems;
    const unitIds = [];
    let targetUnitId = null;
    let relatedPeopleInfo, relatedUnitInfo;
    let localUtilizationByUnitId = {};
    let remoteUtilizationByUnitId;
    let alreadyAssigned = 0;

    if (task.assignPeopleToUnit) {
      const unitFeatureItem = task.assignPeopleToUnit.unitFeatureItem;
      let unitId = aiimUtil.getAttributeValue(unitFeatureItem.feature.attributes,FieldNames.UNIT_ID);
      if (typeof unitId === "string" && unitId.length > 0) {
        targetUnitId = unitId;
        if (unitIds.indexOf(unitId) === -1) unitIds.push(unitId);
      }
    }

    const gids = [];
    const gidfld = sourceUtil.getPeopleLayer().globalIdField;
    peopleFeatureItems.forEach(featureItem => {
      let unitId = aiimUtil.getAttributeValue(featureItem.feature.attributes,FieldNames.UNIT_ID);
      gids.push(featureItem.feature.attributes[gidfld]);
      if (typeof unitId === "string" && unitId.length > 0) {
        if (unitIds.indexOf(unitId) === -1) unitIds.push(unitId);
        if (localUtilizationByUnitId.hasOwnProperty(unitId)) {
          let u = localUtilizationByUnitId[unitId];
          localUtilizationByUnitId[unitId] = (u + 1);
        } else {
          localUtilizationByUnitId[unitId] = 1;
        }
        if (unitId === targetUnitId) {
          alreadyAssigned++;
        }
      }
    });

    const makePeopleEdits = () => {
      const updates = [], undos = [];
      const layer = sourceUtil.getPeopleLayer();
      const fields = layer.fields;
      const keyField = layer.globalIdField;

      peopleFeatureItems.forEach(featureItem => {
        undos.push(clonePersonFeature(task,featureItem.feature,"ensure-geometry"));

        const update = {attributes: {}};
        update.attributes[keyField] = featureItem.feature.attributes[keyField];

        if (task.assignPeopleToUnit) {
          addAttributeEdit(fields,update.attributes,FieldNames.PEOPLE_AREA_ID,null);
          const unitFeatureItem = task.assignPeopleToUnit.unitFeatureItem;
          const unitAttributes = unitFeatureItem.feature.attributes;
          let unitGeometry = unitFeatureItem.geometry || unitFeatureItem.feature.geometry;
          unitBasedFieldsForPeople.forEach(f => {
            let v = aiimUtil.getAttributeValue(unitAttributes,f);
            if (f === "unit_name") v = aiimUtil.getAttributeValue(unitAttributes,"name");
            addAttributeEdit(fields,update.attributes,f,v);
          });
          if (unitGeometry && typeof unitGeometry.clone !== "function") {
            const g = Context.instance.lib.esri.Graphic.fromJSON({
               attributes: unitAttributes,
               geometry: unitGeometry
            });
            unitGeometry = g.geometry;
          }
          update.geometry = unitGeometry.clone().centroid;
        }

        if (task.assignPeopleToArea) {
          appendPersonUnassignment(task,fields,update);
          const areaItem = task.assignPeopleToArea.areaItem;
          const areaId = aiimUtil.getAttributeValue(areaItem.attributes,FieldNames.AREA_ID);
          addAttributeEdit(fields,update.attributes,FieldNames.PEOPLE_AREA_ID,areaId);
        }

        if (task.unassignPeople) {
          appendPersonUnassignment(task,fields,update);
        }

        updates.push(update);
      });

      if (task.assignPeopleToUnit &&
          task.assignPeopleToUnit.unassignCurrentOccupants &&
          relatedPeopleInfo) {
        const keys2 = relatedPeopleInfo.globalIds;
        const features2 = (relatedPeopleInfo && relatedPeopleInfo.features) || [];
        const updates2 = makeUnassignPeopleUpdates(task,keys2,keyField,gids);
        if (updates2.length > 0) {
          updates2.forEach(update2 => {
            updates.push(update2);
          });
          features2.forEach(feature2 => {
            undos.push(clonePersonFeature(task,feature2,"ensure-geometry"));
          });
        }
      }

      if (updates.length > 0) {
        transactionInfo.peopleEdits = {updates: updates};
        transactionInfo.undo.peopleEdits = {updates: undos};
        transactionInfo.edits.push({
          id: layer.layerId,
          updates: updates
        });
        transactionInfo.undo.edits.push({
          id: layer.layerId,
          updates: undos
        });
      }
    };

    const makeUnitEdits = () => {
      const updates = [], undos = [];
      const layer = sourceUtil.getUnitsLayer();
      const fields = layer.fields;
      const keyField = layer.globalIdField;

      const features = (relatedUnitInfo && relatedUnitInfo.features) || [];
      features.forEach(feature => {
        undos.push(cloneUnitFeature(task,feature,"no-geometry"));
      });

      const keys = (relatedUnitInfo && relatedUnitInfo.globalIds) || [];
      keys.forEach(key => {
        let unitId = relatedUnitInfo.unitIdsByGlobalId[key];

        let utilization = 0;
        let localUtilization = 0;
        let remoteUtilization = 0;
        if (localUtilizationByUnitId.hasOwnProperty(unitId)) {
          localUtilization = localUtilizationByUnitId[unitId];
        }
        if (remoteUtilizationByUnitId.hasOwnProperty(unitId)) {
          remoteUtilization = remoteUtilizationByUnitId[unitId];
        }
        if (task.assignPeopleToUnit) {
          if (unitId === targetUnitId) {
            if (task.assignPeopleToUnit.unassignCurrentOccupants) {
              utilization = peopleFeatureItems.length;
            } else {
              utilization = remoteUtilization + peopleFeatureItems.length - alreadyAssigned;
            }
          } else {
            utilization = remoteUtilization - localUtilization;
          }
        }
        if (task.assignPeopleToArea) {
          utilization = remoteUtilization - localUtilization;
        }
        if (task.unassignPeople) {
          utilization = remoteUtilization - localUtilization;
        }
        if (utilization < 0) utilization = 0;

        let asnType = OfficePlan.SpaceAssignmentTypes.none
        if (utilization > 0) {
          asnType = OfficePlan.SpaceAssignmentTypes.office;
        }

        // TODO validate these?
        const update = {attributes: {}};
        update.attributes[keyField] = key;
        addAttributeEdit(fields,update.attributes,FieldNames.UNITS_AREA_ID,null);
        addAttributeEdit(fields,update.attributes,FieldNames.UNITS_SPACE_ASSIGNMENT_TYPE,asnType);
        addAttributeEdit(fields,update.attributes,FieldNames.UTILIZATION, utilization);
        updates.push(update);
      });

      if (updates.length > 0) {
        transactionInfo.unitEdits = {updates: updates};
        transactionInfo.undo.unitEdits = {updates: undos};
        transactionInfo.edits.push({
          id: layer.layerId,
          updates: updates
        });
        transactionInfo.undo.edits.push({
          id: layer.layerId,
          updates: undos
        });
      }
    };

    Promise.resolve().then(() => {
      return fetchRelatedUnits(task,unitIds).then(result => {
        relatedUnitInfo = result;
        console.log("fetchRelatedUnits",relatedUnitInfo);
      });
    }).then(() => {
      if (task.assignPeopleToUnit &&
          task.assignPeopleToUnit.unassignCurrentOccupants &&
          targetUnitId) {
        return fetchRelatedPeople(task,[targetUnitId]).then(result => {
          relatedPeopleInfo = result;
          console.log("fetchRelatedPeople",relatedPeopleInfo);
        });
      }
    }).then(() => {
      return fetchUtilization(unitIds).then(result => {
        remoteUtilizationByUnitId = result.utilizationByUnitId;
        console.log("fetchUtilization.result",result);
        console.log("localUtilizationByUnitId",localUtilizationByUnitId)
        console.log("remoteUtilizationByUnitId",remoteUtilizationByUnitId)
      });
    }).then(() => {
      makePeopleEdits();
      makeUnitEdits();
    }).then(() => {
      resolve(transactionInfo);
    }).catch(ex => {
      reject(ex);
    });

  });
  return promise;
}
